import React from 'react';
import './StreakBlock.css';
import {useTranslation} from 'react-i18next';
import {useStreak} from '../../../hooks/useStreak';
import {removeDangereous} from "../../../utils/removeDangereous";
import {useDisplayMode} from "../../../hooks/useDisplayMode";
import {ViewWrapper} from "../../../components/ViewWrapper/ViewWrapper";

interface DayStreak {
  day: string;
  date: Date;
  isCompleted: boolean;
}

interface StreakBlockProps {
  // When provided, this overrides internal loading and controls the skeleton visibility
  loading?: boolean;
}

const StreakBlock: React.FC<StreakBlockProps> = ({loading: externalLoading}) => {
  const {t} = useTranslation();
  const {isDesktop} = useDisplayMode();
  const {
    streakData,
    loading: streakLoading,
    error,
    // updateActivity,
    fetchStreakData
  } = useStreak();

  // const handleActivityUpdate = async () => {
  //   const response = await updateActivity();
  //   if (response) {
  //     // You could show a toast notification here
  //     console.log('Activity updated:', response.message);
  //   }
  // };

  // Show 7 days (current week) with proper streak indication
  const days: DayStreak[] = React.useMemo(() => {
    const result: DayStreak[] = [];
    const today = new Date();

    // Get the start of the current week (Sunday)
    const startOfWeek = new Date(today);
    const dayOfWeek = today.getDay();
    startOfWeek.setDate(today.getDate() - dayOfWeek);

    for (let i = 0; i < 7; i++) {
      const d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);

      // Check if this day should be marked as completed
      let isCompleted = false;
      if (streakData) {
        const dateStr = d.toISOString().split('T')[0]; // YYYY-MM-DD format

        if (d.toDateString() === today.toDateString()) {
          // Today - check if user is active today
          isCompleted = streakData.isActiveToday;
        } else if (d < today) {
          // Past days - check if there was activity using activityHistory
          isCompleted = streakData.activityHistory.includes(dateStr);
        }
      }

      result.push({
        day: d.toLocaleDateString(t('language'), {weekday: 'short'}),
        date: d,
        isCompleted,
      });
    }
    return result;
  }, [t, streakData]);

  const todayIndex = React.useMemo(() => new Date().getDay(), []);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const todayRef = React.useRef<HTMLDivElement | null>(null);

  const isLoading = externalLoading ?? streakLoading;

  React.useEffect(() => {
    if (isLoading || error) return;
    const el = todayRef.current;
    const container = containerRef.current;
    if (!el || !container) return;
    const reduceMotion = typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    try {
      el.scrollIntoView({behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'center'});
    } catch (e) {
      const elRect = el.getBoundingClientRect();
      const contRect = container.getBoundingClientRect();
      const currentScroll = container.scrollLeft || 0;
      const offset = (elRect.left - contRect.left) - (contRect.width / 2 - elRect.width / 2);
      container.scrollLeft = currentScroll + offset;
    }
  }, [isLoading, error, days, todayIndex]);

  if (isLoading) {
    return (
      <div className="streak-block skeleton" role="status" aria-busy="true" aria-live="polite">
        <div className="streak_block_padding">
          <div className="streak-header">
            <div className="skeleton-bar skeleton-shimmer" aria-hidden="true"/>
          </div>
          <div className="streak-days" aria-hidden="true">
            {Array.from({length: 5}).map((_, idx) => (
              <div key={idx} className="streak-day">
                <div className="skeleton-circle skeleton-shimmer"/>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="streak-block">
        <div className="streak_block_padding">
          <p className="streak-message">{t('product.courses.streakError')}: {error}</p>
          <button onClick={fetchStreakData} className="retry-button">
            {t('product.courses.retry')}
          </button>
        </div>
      </div>
    );
  }

  const currentStreak = streakData?.currentStreak || 0;

  return (
    <div className="streak-block">
      <div className="streak_block_padding">
        <div className="streak-header">
          {currentStreak > 0 ? (
            <p
              className="streak-message"
              dangerouslySetInnerHTML={{__html: removeDangereous(t('product.courses.streakMessage', {count: currentStreak}))}}
            />
          ) : (
            <p className="streak-message"
               dangerouslySetInnerHTML={{__html: removeDangereous(t('product.courses.startStreak'))}}>
            </p>
          )}
        </div>
        <ViewWrapper enable={isDesktop}>
          <div className="divider vertical"/>
        </ViewWrapper>
        <div className="streak-days" ref={containerRef}>
          {days.map((day, index) => {
            const isToday = index === todayIndex;
            // const isPast = day.date < new Date();

            let circleClass = 'flame-circle';
            if (day.isCompleted) circleClass += ' completed';
            // if (isToday) circleClass += ' today';
            // if (isPast && !day.isCompleted) circleClass += ' past';

            return (
              <div key={index} className="streak-day" ref={isToday ? todayRef : undefined}
                   data-today={isToday ? 'true' : undefined}>
                <div className={circleClass}>
                  <img
                    src={day.isCompleted ? '/images/ui/flame-fill.png' : '/images/ui/flame.png'}
                    alt={day.isCompleted ? 'completed' : 'flame'}
                    className="flame-icon"
                  />
                </div>
                <span className="day-label">{day.day}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StreakBlock;
